// Distance from the resident to a pin, and how we say it in Polish.

export type LatLon = { lat: number; lon: number };

const R = 6371000;
const toRad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in metres. */
export function haversine(a: LatLon, b: LatLon): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * The app's one distance formatter: „930 m" up to a kilometre, then „1,3 km",
 * with the Polish comma. Every distance a resident reads comes through here —
 * the axis's third column, the detail cards, the rings and the radius — so two
 * places at the same distance always read the same.
 *
 * Two rules, and the second is the one that is easy to miss. The grain is 10 m,
 * which is what §7.2 names and what the prototype's own rows show; a 50 m step
 * rounds „930 m" and „970 m" onto one number. And the unit is chosen **after**
 * the rounding, not before: at 997 m the metres round to 1000, and „1000 m" is
 * a number this app never writes, because the kilometre is where it stops
 * counting in tens.
 */
export function distanceLabel(metres: number): string {
  const tens = Math.round(metres / 10) * 10;
  if (tens < 1000) return `${tens} m`;
  return `${(metres / 1000).toFixed(1).replace('.', ',')} km`;
}

/** The resident's position, once they have allowed it. Nothing is asked for here. */
export const here = {
  at: null as LatLon | null,
  listeners: new Set<() => void>(),
  set(p: LatLon) {
    this.at = p;
    for (const fn of this.listeners) fn();
  },
  /**
   * Forget it. Having a position and being allowed to use it are one fact, not
   * two (#279): the moment the permission is gone the last fix goes with it, or
   * the centre could still resolve to „ja" while the note says we have no
   * access. `geolocate` is the only caller, because the permission is its
   * subject.
   *
   * Silent when there is nothing to forget: the same store that fires once on a
   * fix arriving fires once on it going away, and never for a no-op.
   */
  clear() {
    if (!this.at) return;
    this.at = null;
    for (const fn of this.listeners) fn();
  },
  /** Like every other store here, returns the disposer: a re-mount must be able to let go. */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  },
  /** Distance to a place, or null when we do not know where either end is. */
  to(p: { lat: number | null; lon: number | null }): number | null {
    if (!this.at || p.lat == null || p.lon == null) return null;
    return haversine(this.at, { lat: p.lat, lon: p.lon });
  },
};
